import { toast } from "react-hot-toast";
import { createStore } from "zustand";

import sleep from "../../utils/sleep";

import { envMapKeys, viteEnvMap } from "./constants";
import { urlMap } from "../../constants/URL";
import { setupSentry } from "utils/setupSentry";

let isFetching = false;

export type TEnvStore = {
  [K in keyof typeof envMapKeys]: string;
} & {
  version: string | null;
  assignState: (envs: Record<string, string>) => void;
  fetchEnvs: () => Promise<{
    keys: Record<string, string>;
    version: string;
  }>;
};

export const evalShouldFetchEnvs = () => {
  if (isFetching) {
    return false;
  }

  if (
    !window.localStorage.getItem("envs") ||
    !window.localStorage.getItem("env_version")
  ) {
    return true;
  }

  const parsedEnvs = JSON.parse(window.localStorage.getItem("envs")!);
  const isEmpty = Object.keys(parsedEnvs).length === 0;
  if (isEmpty) {
    return true;
  }

  return false;
};

const getInitialEnvs = () => {
  const envs = JSON.parse(window.localStorage.getItem("envs") || "{}");
  return Object.keys(envMapKeys).reduce((acc, key) => {
    const { frontend, backend } = envMapKeys[key as keyof typeof envMapKeys];
    return {
      ...acc,
      [key]: envs[backend] || viteEnvMap[frontend],
    };
  }, {} as Record<keyof typeof envMapKeys, string>);
};

const checkIfShouldRefresh = async (
  storeVersion: string,
  requestVersion: string
) => {
  // Refresh if the version is different or doesn't exist in local storage
  const localVersion =
    window.localStorage.getItem("env_version") || storeVersion;

  const mustRefresh = (() => {
    if (!!localVersion && !isNaN(Number(localVersion))) {
      return Number(localVersion) !== Number(requestVersion);
    }

    return false;
  })();

  if (mustRefresh) {
    toast("Application config has been updated. Refreshing...");
    await sleep(1000);
    window.location.reload();
  }

  return false;
};

export const createEnvStore = () => {
  const store = createStore<TEnvStore>((set, get) => {
    return {
      ...getInitialEnvs(),
      version: window.localStorage.getItem("env_version"),
      assignState: (envs) =>
        set((state) => ({
          ...state,
          ...Object.keys(envMapKeys).reduce((acc, key) => {
            const { backend, frontend } =
              envMapKeys[key as keyof typeof envMapKeys];

            return {
              ...acc,
              [key]: envs[backend] || viteEnvMap[frontend],
            };
          }, {}),
        })),
      fetchEnvs: async () => {
        try {
          isFetching = true;
          // NOTE: removed a redundant `await import("constants/index")` here. `urlMap` is
          // already statically imported above, so the barrel re-import added nothing — and
          // under Vite/Rollup ESM it triggered a circular-dependency TDZ crash
          // ("Cannot access 'index$1' before initialization") because fetchEnvs() runs during
          // store module init (see bottom of this file). CRA/webpack (CJS) tolerated it.
          const response = await fetch(
            urlMap.v2 + "user-dashboard/fetch-organization-config",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          //const response:any = {};
          isFetching = false;

          if (response.ok === false) {
            throw new Error("Failed to fetch envs");
          }

          const data = await response.json();

          await checkIfShouldRefresh(get().version!, data.version);
          localStorage.setItem("envs", JSON.stringify(data.keys));
          localStorage.setItem("env_version", data.version);
          get().assignState(data.keys);
          set({ version: data.version });
          return data;
        } catch (e) {
          isFetching = false;
          throw new Error("Failed to fetch envs");
        }
      },
    };
  });

  const shouldFetchEnvs = evalShouldFetchEnvs();
  if (shouldFetchEnvs) {
    store.getState().fetchEnvs();
  }
  const { sentry_dsn, sentry_environment } = store.getState();

  if (sentry_dsn && sentry_environment) {
    setupSentry({
      sentry_dsn,
      sentry_environment,
    });
  }

  return store;
};
